/* Разбивка платежа по счетам: 20 тысяч с одного, 50 с другого.
   Без `total` (регулярный расход) сумма платежа — сумма долей.
   С `total` (составная операция) сумма задана заранее, а доли её раскладывают:
   новая строка получает остаток, «Поровну» делит всё на равные части,
   под списком видно, сколько ещё не разложено. */

import { rub } from "@mera/shared";
import { List, ListItem } from "konsta/react";
import React from "react";

import { Ion } from "./icons.jsx";
import { haptic } from "../lib/tg.js";

const cents = (v) => Math.round((Number(v) || 0) * 100);

export default function SplitsEditor({ caption, splits, accounts, onChange, total: target }) {
  const rows = splits || [];
  const total = rows.reduce((s, r) => s + cents(r.amount), 0) / 100;
  const fixed = target !== undefined;
  const left = fixed ? (cents(target) - cents(total)) / 100 : 0;

  const patch = (i, key) => (e) =>
    onChange(rows.map((r, n) => (n === i ? { ...r, [key]: e.target.value } : r)));
  const remove = (i) => () => { haptic.light(); onChange(rows.filter((_, n) => n !== i)); };
  const add = () => {
    haptic.light();
    // Подставляем первый ещё не занятый счёт — обычно именно его и хотят
    const free = accounts.find((a) => !rows.some((r) => r.acc === a.id));
    onChange([...rows, { acc: free?.id || "", amount: left > 0 ? String(left) : "" }]);
  };
  /* Поровну до копейки: что не делится, достаётся последней доле,
     чтобы сумма долей ровно сошлась с платежом */
  const even = () => {
    haptic.light();
    const all = cents(target);
    const part = Math.floor(all / rows.length);
    onChange(rows.map((r, i) => ({
      ...r,
      amount: String((i === rows.length - 1 ? all - part * (rows.length - 1) : part) / 100),
    })));
  };

  return (
    <div className="mt-8">
      <div className="px-8 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        {caption}
      </div>
      <List strong inset className="!my-0">
        {rows.map((r, i) => (
          <li key={i} className="hairline-b">
            <div className="flex items-center gap-2 ps-4 pe-3 py-2.5">
              <select
                className="min-w-0 flex-1 bg-transparent outline-none text-[15px] truncate"
                style={{ color: "var(--tg-text)" }}
                value={r.acc}
                onChange={patch(i, "acc")}
              >
                <option value="">Счёт не выбран</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.biz} / {a.name}</option>)}
              </select>
              <input
                className="num w-28 shrink-0 bg-transparent outline-none text-[17px] text-right"
                style={{ color: "var(--tg-text)" }}
                type="number"
                inputMode="decimal"
                placeholder="0 ₽"
                value={r.amount}
                onChange={patch(i, "amount")}
              />
              <button
                type="button"
                onClick={remove(i)}
                aria-label="Убрать счёт"
                className="shrink-0 w-7 h-7 inline-flex items-center justify-center active:opacity-50"
              >
                <Ion.close size={18} style={{ color: "var(--fin-red)" }} />
              </button>
            </div>
          </li>
        ))}
        <ListItem
          link
          chevron={false}
          colors={{ primaryTextIos: "text-[color:var(--tg-link)]" }}
          media={<Ion.add size={20} style={{ color: "var(--tg-link)" }} />}
          title="Добавить счёт"
          onClick={add}
        />
      </List>

      {fixed ? (
        <div className="px-8 pt-1.5 flex items-center justify-between gap-3 text-[13px]">
          <span style={{ color: left === 0 ? "var(--tg-hint)" : "var(--fin-red)" }}>
            {left === 0
              ? <>Разложено <span className="num">{rub(total)}</span> на {rows.length} сч.</>
              : left > 0
                ? <>Осталось разложить <span className="num">{rub(left)}</span></>
                : <>Доли больше платежа на <span className="num">{rub(-left)}</span></>}
          </span>
          {rows.length > 1 && cents(target) > 0 ? (
            <button type="button" onClick={even} className="shrink-0 font-medium active:opacity-50"
              style={{ color: "var(--tg-link)" }}>
              Поровну
            </button>
          ) : null}
        </div>
      ) : (
      <div className="px-8 pt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        {rows.length > 1
          ? <>За раз уходит <span className="num">{rub(total)}</span> с {rows.length} счетов.</>
          : <>За раз уходит <span className="num">{rub(total)}</span>.</>}
      </div>
      )}
    </div>
  );
}
